import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import {
  ColDef,
  IRowNode,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-community";

import Project from "models/ProjectBasicInfo";
import ProjectStatusBadge from "components/ProjectStatusBadge";

import styles from "./ProjectTable.module.scss";
import { ReactComponent as Delete } from "assets/icons/delete.svg";

interface ProjectTableProps {
  data: Project[];
  textFilter?: string;
  statusFilter?: number;
  onDelete: (project_id: string) => void;
  onOpenProject: (project_id: string) => void;
}

const StatusCell = (props: CustomCellRendererProps) => {
  const status = props.value;
  return (
    <div className={styles.customCell}>
      <ProjectStatusBadge status={status} />
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ActionCell = (props: any) => {
  return (
    <div className={styles.customCell}>
      <Delete
        className={styles.deleteButton}
        onClick={() => props.onDelete(props.value)}
      />
    </div>
  );
};

const ProjectTable = ({
  data,
  textFilter,
  statusFilter,
  onDelete,
  onOpenProject,
}: ProjectTableProps) => {
  const gridRef = useRef<AgGridReact<Project>>(null);
  const [colDefs] = useState<ColDef<Project>[]>([
    {
      field: "name",
      headerName: "Name",
      onCellDoubleClicked: (e) => e.data && onOpenProject(e.data.id),
    },
    {
      field: "last_request_timestamp",
      headerName: "Last Request",
      valueFormatter: (params) => params.value ?? "-",
    },
    { field: "status", headerName: "Status", cellRenderer: StatusCell },
    { field: "inference_mode", headerName: "Deployment Mode" },
    {
      headerName: "Actions",
      cellRenderer: ActionCell,
      width: 100,
      valueGetter: (params) => params.data?.id,
      cellRendererParams: {
        onDelete,
      },
    },
  ]);

  const autoSizeStrategy = useMemo<
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy
  >(() => {
    return {
      type: "fitGridWidth",
    };
  }, []);

  useEffect(() => {
    gridRef?.current?.api?.onFilterChanged();
  }, [textFilter, statusFilter]);

  const isExternalFilterPresent = useCallback((): boolean => {
    return (
      (textFilter !== undefined && textFilter.length > 0) ||
      statusFilter !== undefined
    );
  }, [textFilter, statusFilter]);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<Project>): boolean => {
      if (node.data) {
        const containsText =
          textFilter === undefined ||
          node.data.name.toLowerCase().includes(textFilter.toLowerCase());
        const containsStatus =
          statusFilter === undefined ||
          statusFilter === -1 ||
          node.data.status == statusFilter;

        return containsText && containsStatus;
      }
      return true;
    },
    [textFilter, statusFilter],
  );

  return (
    <AgGridReact
      ref={gridRef}
      rowData={data}
      columnDefs={colDefs}
      isExternalFilterPresent={isExternalFilterPresent}
      doesExternalFilterPass={doesExternalFilterPass}
      autoSizeStrategy={autoSizeStrategy}
    />
  );
};

export default ProjectTable;
